import React, { useState, useCallback, useMemo } from 'react';

import WinXP from 'WinXP';
import { CartProvider } from 'contexts/CartContext';
import { CRTProvider, useCRT } from 'contexts/CRTContext';
import { ProductsProvider, useProducts } from 'contexts/ProductsContext';
import CRTEffect from 'components/CRTEffect';
import BootScreen from 'components/BootScreen';
import MobileBinco from 'WinXP/apps/Binco/MobileBinco';
import useIsMobile from 'hooks/useIsMobile';
import { Analytics } from '@vercel/analytics/react';
import { SizePreferenceProvider } from 'contexts/SizePreference';
import { decodeProductId } from 'lib/shareUtils';

function slugify(str) {
  return String(str || '')
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function AppInner() {
  const { crtEnabled } = useCRT();
  const { products } = useProducts();
  const isMobile = useIsMobile(786);
  const [booted, setBooted] = useState(false);
  const onBootDone = useCallback(() => setBooted(true), []);

  // Check for shared product link ?p=xxxxx or ?handle=xyz
  const sharedProduct = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    if (products.length === 0) return null;

    // Check ?p= (encoded ID from share links)
    const code = params.get('p');
    if (code) {
      const shopifyId = decodeProductId(code);
      if (shopifyId) {
        const found = products.find(p => p.id === shopifyId);
        if (found) { window.history.replaceState({}, '', '/'); return found; }
      }
    }

    // Check ?handle= param or /product/handle path
    const handle = params.get('handle') || (window.location.pathname.startsWith('/product/') ? window.location.pathname.replace('/product/', '') : null);
    if (handle) {
      const found = products.find(p => p.handle === handle);
      if (found) { window.history.replaceState({}, '', '/'); return found; }
    }

    return null;
  }, [products]);

  // Detect category & optional sub-brand from path
  // /bands, /games, /goofy, /rep — category only
  // /bands/trivium, /games/halo-3, /rep/champion — category + brand
  const { sharedCategory, sharedBrand } = useMemo(() => {
    const path = window.location.pathname.replace(/^\/|\/$/g, '').toLowerCase();
    const slugToCategory = { bands: 'music', music: 'music', games: 'games', goofy: 'goofy', rep: 'rep' };
    if (!path) return { sharedCategory: null, sharedBrand: null };

    const segments = path.split('/');
    const cat = slugToCategory[segments[0]];
    if (!cat) return { sharedCategory: null, sharedBrand: null };

    let brand = null;
    if (segments[1] && products.length > 0) {
      // Find matching band/game/brand by slug
      const targetField = cat === 'music' ? 'band' : cat === 'games' ? 'game' : cat === 'rep' ? 'brand' : null;
      if (targetField) {
        const match = products.find(p => p[targetField] && slugify(p[targetField]) === segments[1]);
        if (match) brand = match[targetField];
      }
    }

    if (cat) window.history.replaceState({}, '', '/');
    return { sharedCategory: cat, sharedBrand: brand };
  }, [products]);

  return (
    <>
      {!booted && <BootScreen onDone={onBootDone} />}
      {isMobile ? (
        <MobileBinco sharedProduct={sharedProduct} sharedCategory={sharedCategory} sharedBrand={sharedBrand} />
      ) : (
        <>
          <WinXP sharedProduct={sharedProduct} sharedCategory={sharedCategory} sharedBrand={sharedBrand} />
          <CRTEffect enabled={crtEnabled} />
        </>
      )}
    </>
  );
}

const App = () => {
  return (
    <CRTProvider>
      <ProductsProvider>
        <CartProvider>
          <SizePreferenceProvider>
            <AppInner />
            <Analytics />
          </SizePreferenceProvider>
        </CartProvider>
      </ProductsProvider>
    </CRTProvider>
  );
};

export default App;
